import { useState } from "react"
import { useTranslation } from "@/i18n"
import { useAuth } from "@/state/AuthContext"
import { useSettings } from "@/state/SettingsContext"
import { useConfig, useConfigMeta } from "@/config/context"
import { useNostr } from "@/state/NostrContext"
import type { ThemeName, ThemeSetting } from "@/config/types"
import { relayInfoService } from "@/nostr/relayInfo"
import { AddRelayForm } from "@/components/AddRelayForm"
import { RelayLibraryEditor } from "@/components/RelayLibraryEditor"
import { LoginDialog } from "@/components/LoginDialog"
import { Avatar } from "@/components/Avatar"
import { Icon } from "@/components/Icon"
import { simplifyUrl } from "@/utils/url"
import { shortNpub } from "@/utils/nip19"

/** Settings: appearance, feeds, relays, account and diagnostics. */
export function SettingsPage() {
  const { t, languages, setLanguage, language } = useTranslation()
  const config = useConfig()
  const meta = useConfigMeta()
  const {
    settings,
    setTheme,
    setIncludeReplies,
    setMutedWords,
    setHideOnionRelays,
    setSearchRelays,
    resetSettings,
  } = useSettings()
  const { connectedCount, statuses } = useNostr()
  const { pubkey, method, canSign, accounts, logout, loginWithNpub } = useAuth()
  const [loginOpen, setLoginOpen] = useState(false)
  const [mutedDraft, setMutedDraft] = useState(settings.mutedWords.join(", "))
  const [npubInput, setNpubInput] = useState("")

  const themes: ThemeName[] = config.design.theme.allowed
  const themeLabels: Record<ThemeName, string> = {
    light: t("Light"),
    dark: t("Dark"),
    "pure-black": t("Pure black"),
  }
  const searchRelays =
    settings.searchRelays.length > 0 ? settings.searchRelays : config.relays.searchRelays

  return (
    <div className="page settings-page">
      <header className="page-header">
        <div className="page-header-main">
          <h1 className="page-title">{t("Settings")}</h1>
          <p className="page-subtitle">
            {config.branding.name} · {config.branding.description}
          </p>
        </div>
      </header>

      <nav className="settings-nav" aria-label={t("Settings")}>
        <a href="#appearance">{t("Appearance")}</a>
        <a href="#feeds">{t("Feed")}</a>
        <a href="#relays">{t("Relays")}</a>
        <a href="#account">{t("Account")}</a>
        <a href="#about">{t("About")}</a>
      </nav>

      <section className="settings-section" id="appearance">
        <h2>{t("Appearance")}</h2>
        <div className="setting-row">
          <div>
            <p className="setting-label">{t("Theme")}</p>
            <p className="muted small">The palette is applied before the first paint.</p>
          </div>
          <div className="segmented" role="group" aria-label={t("Theme")}>
            {themes.map((theme) => (
              <button
                key={theme}
                type="button"
                className={`segment ${settings.theme === theme ? "active" : ""}`}
                onClick={() => setTheme(theme as ThemeSetting)}
                aria-pressed={settings.theme === theme}
              >
                {themeLabels[theme]}
              </button>
            ))}
            <button
              type="button"
              className={`segment ${settings.theme === "system" ? "active" : ""}`}
              onClick={() => setTheme("system")}
              aria-pressed={settings.theme === "system"}
            >
              {t("System")}
            </button>
          </div>
        </div>

        <div className="setting-row">
          <div>
            <p className="setting-label">{t("Language")}</p>
            <p className="muted small">Interface language; missing strings fall back to English.</p>
          </div>
          <select
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            aria-label={t("Language")}
          >
            {languages.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="settings-section" id="feeds">
        <h2>{t("Feed")}</h2>
        <label className="setting-row">
          <div>
            <p className="setting-label">{t("Include replies in the feed")}</p>
            <p className="muted small">Replies are hidden by default so relay feeds stay readable.</p>
          </div>
          <input
            type="checkbox"
            checked={settings.includeReplies}
            onChange={(event) => setIncludeReplies(event.target.checked)}
          />
        </label>

        <label className="setting-row">
          <div>
            <p className="setting-label">Hide .onion relays</p>
            <p className="muted small">Tor relays cannot be reached from a browser without a proxy.</p>
          </div>
          <input
            type="checkbox"
            checked={settings.hideOnionRelays}
            onChange={(event) => setHideOnionRelays(event.target.checked)}
          />
        </label>

        <div className="setting-row setting-row-column">
          <div>
            <p className="setting-label">{t("Muted words")}</p>
            <p className="muted small">
              Comma separated. Notes containing any of these words are hidden locally.
            </p>
          </div>
          <div className="inline-form">
            <input
              value={mutedDraft}
              onChange={(event) => setMutedDraft(event.target.value)}
              placeholder="spam, giveaway"
              aria-label={t("Muted words")}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() =>
                setMutedWords(
                  mutedDraft
                    .split(",")
                    .map((word) => word.trim())
                    .filter(Boolean),
                )
              }
            >
              {t("Save")}
            </button>
          </div>
        </div>
      </section>

      <section className="settings-section" id="relays">
        <h2>{t("Relays")}</h2>
        <RelayLibraryEditor />

        <div className="settings-subsection">
          <h3>{t("Search relays")}</h3>
          <p className="muted small">
            NIP-50 relays used by the search page. Leave empty to use the configured defaults.
          </p>
          <ul className="relay-list">
            {searchRelays.map((url) => (
              <li key={url} className="relay-list-item">
                <Icon name="search" size={16} />
                <span className="relay-list-url">{simplifyUrl(url)}</span>
                <button
                  type="button"
                  className="icon-button small"
                  aria-label={`Remove search relay ${url}`}
                  onClick={() =>
                    setSearchRelays(
                      (settings.searchRelays.length > 0 ? settings.searchRelays : searchRelays).filter(
                        (item) => item !== url,
                      ),
                    )
                  }
                >
                  <Icon name="trash" size={15} />
                </button>
              </li>
            ))}
          </ul>
          <AddRelayForm
            buttonLabel="Add search relay"
            onAdd={(urls) =>
              setSearchRelays([
                ...new Set([
                  ...(settings.searchRelays.length > 0 ? settings.searchRelays : searchRelays),
                  ...urls,
                ]),
              ])
            }
          />
        </div>
      </section>


      <section className="settings-section" id="account">
        <h2>{t("Account")}</h2>
        {pubkey ? (
          <>
            <div className="setting-row">
              <div className="account-summary">
                <Avatar label={shortNpub(pubkey)} size={44} />
                <div>
                  <p className="account-name">{shortNpub(pubkey)}</p>
                  <p className="muted small">
                    {method} · {canSign ? "can sign" : "read-only"}
                  </p>
                </div>
              </div>
              <button type="button" className="btn btn-ghost" onClick={logout}>
                <Icon name="logout" size={16} /> {t("Logout")}
              </button>
            </div>
            {accounts.length > 1 && (
              <p className="muted small">
                {accounts.length} saved accounts — switch them from the account menu.
              </p>
            )}
          </>
        ) : (
          <>
            <p className="muted small">
              You are browsing anonymously. Relay feeds, relay discovery and search work without an account;
              publishing and following need a signer.
            </p>
            <div className="inline-form">
              <button type="button" className="btn btn-primary" onClick={() => setLoginOpen(true)}>
                <Icon name="user" size={16} /> {t("Log in")}
              </button>
            </div>
            <form
              className="inline-form"
              onSubmit={(event) => {
                event.preventDefault()
                loginWithNpub(npubInput)
              }}
            >
              <input
                value={npubInput}
                onChange={(event) => setNpubInput(event.target.value)}
                placeholder="npub1… (read-only)"
                aria-label={t("Public key")}
              />
              <button type="submit" className="btn btn-ghost" disabled={npubInput.length < 10}>
                {t("Read only")}
              </button>
            </form>
          </>
        )}
      </section>

      <section className="settings-section" id="about">
        <h2>{t("About")}</h2>
        <div className="setting-row">
          <div>
            <p className="setting-label">
              {config.branding.name} — v{config.version}
            </p>
            <p className="muted small">{config.branding.description}</p>
          </div>
        </div>
        <div className="setting-row">
          <div>
            <p className="setting-label">Configuration layers applied</p>
            <p className="muted small">{meta.sources.join(" → ")}</p>
            {meta.warnings.length > 0 && (
              <ul className="config-warnings">
                {meta.warnings.slice(0, 5).map((warning) => (
                  <li key={warning} className="muted small">
                    {warning}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="setting-row">
          <div>
            <p className="setting-label">Relay connections</p>
            <p className="muted small">
              {connectedCount} connected of {statuses.length} known relays
            </p>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              relayInfoService.clear()
              resetSettings()
            }}
          >
            <Icon name="refresh" size={16} /> Reset cached metadata & settings
          </button>
        </div>
        <ul className="about-links">
          {config.branding.links.map((link) => (
            <li key={link.url}>
              <a href={link.url} target="_blank" rel="noopener noreferrer">
                <Icon name={link.icon ?? "link"} size={14} /> {link.label}
              </a>
            </li>
          ))}
        </ul>
        {config.branding.footerText && <p className="muted small">{config.branding.footerText}</p>}
      </section>

      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  )
}
